/* "You may also like" — genuine, local, and different every visit.
 *
 * The old section asked /search/suggest.json for the record's first style tag
 * and its artist. That endpoint returns the SAME deterministic top ten for every
 * record sharing a tag, and the shortfall was padded from a fixed best-seller
 * list, so a few records were recommended on hundreds of pages and tempo and
 * label were never considered at all.
 *
 * This scores the whole catalogue locally against the record being viewed —
 * label, artist, shared genre tokens and BPM proximity — keeps everything that
 * genuinely relates, and then draws the shown set at random from that pool,
 * weighted towards the closest matches. The list is real every time and
 * different every time.
 *
 * Card data (image, price, stock) is fetched per page of four from
 * /products/<handle>.js, so nothing but the scoring index is loaded up front.
 */
(function () {
  'use strict';

  var SHOW = 16;        // size of the shuffled list the arrows page through
  var POOL = 90;        // best-scoring records the shown set is drawn from
  var ANCHOR = 3;       // strongest matches, always present but never in a fixed slot
  /* A candidate has to be at least this good RELATIVE to the best match on the
     page to be shown at all. Without it the pool is simply the top 90 by score,
     so when a record has eight genuinely close neighbours the ninetieth — barely
     related — still gets a turn. Rue caught this: an 80 BPM dark-ambient double
     LP appearing under a 1991 rave 12" because both are filed somewhere near
     "techno". Relaxed automatically if too few clear the bar. */
  var FLOOR_RATIO = 0.45;
  var PER_PAGE_DESKTOP = 4;
  var PER_PAGE_MOBILE = 2;

  /* ── scoring weights ───────────────────────────────────────────────────
     Tuned so that a same-label record and a record sharing three genre
     tokens at the same tempo score comparably: neither dimension alone is
     allowed to fill the pool. */
  var W_LABEL = 50;
  var W_ARTIST = 40;
  var W_GENRE = 18;     // per shared token the record genuinely carries
  var W_GENRE_CAP = 54; // ... but three tokens is as good as it gets
  /* Tokens we GUESSED at, by expanding a coarse shop tag like "Techno" into
     every index token containing that word. That is weak evidence and must not
     be paid like the real thing: the word "techno" expands to 43 tokens, so an
     80 BPM dark-ambient double LP tagged "dub techno" was scoring a full genre
     match against a 1991 rave 12" and landing 7th out of 2,452. */
  var W_GENRE_LOOSE = 5;
  var W_GENRE_LOOSE_CAP = 12;
  var W_BPM = 30;       // scaled by closeness
  var BPM_WINDOW = 12;  // beyond this the tempo contributes nothing
  var W_STOCK = 8;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function money(cents) {
    if (cents == null || isNaN(cents)) return '';
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'CAD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: cur
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  /* Split "Artist - Title" the way the rest of the store does. */
  function splitTitle(title, vendor) {
    var parts = title.split(' – ');
    if (parts.length < 2) parts = title.split(' — ');
    if (parts.length < 2) parts = title.split(' - ');
    if (parts.length > 1) {
      return { artist: parts[0], album: parts.slice(1).join(' - ') };
    }
    return { artist: vendor || '', album: title };
  }

  // ── scoring ───────────────────────────────────────────────────────────

  /* Names that are not names. Discogs writes "Not On Label" where a record is
     self-released and "Various" where a compilation has no single artist; both
     arrive here looking like ordinary values. Treating them as real was scoring
     a full label match between a white-label acid house 12" and a Pink Floyd
     compilation — the only thing they share is the absence of a label — and a
     full artist match between all 368 compilations in the shop. */
  var PLACEHOLDER = /^(not on label|no label|none|unknown|unknown artist|self[- ]?released|white ?label|n\/?a|various|various artists|v\/a)$/i;
  var voidLabel = null, voidArtist = null;
  function markPlaceholders(data) {
    if (voidLabel) return;
    voidLabel = Object.create(null);
    voidArtist = Object.create(null);
    var i;
    for (i = 0; i < data.l.length; i++) {
      if (PLACEHOLDER.test(String(data.l[i]).trim())) voidLabel[i] = true;
    }
    for (i = 0; i < data.a.length; i++) {
      if (PLACEHOLDER.test(String(data.a[i]).trim())) voidArtist[i] = true;
    }
  }

  function score(me, other) {
    var s = 0;

    if (me[1] >= 0 && me[1] === other[1] && !voidLabel[me[1]]) s += W_LABEL;
    if (me[2] >= 0 && me[2] === other[2] && !voidArtist[me[2]]) s += W_ARTIST;

    if (me[3].length && other[3].length) {
      var mine = {};
      for (var i = 0; i < me[3].length; i++) mine[me[3][i]] = true;
      var shared = 0;
      for (var j = 0; j < other[3].length; j++) if (mine[other[3][j]]) shared++;
      if (shared) s += Math.min(shared * W_GENRE, W_GENRE_CAP);
    }
    /* me[6] exists only for a record the index has never seen, whose genres we
       inferred from its shop tags. Scored separately and cheaply, so it can
       break a tie but never manufacture a strong match on its own. */
    if (me[6] && me[6].length && other[3].length) {
      var loose = {};
      for (var k = 0; k < me[6].length; k++) loose[me[6][k]] = true;
      var lshared = 0;
      for (var m = 0; m < other[3].length; m++) if (loose[other[3][m]]) lshared++;
      if (lshared) s += Math.min(lshared * W_GENRE_LOOSE, W_GENRE_LOOSE_CAP);
    }

    if (me[4] && other[4]) {
      var d = Math.abs(me[4] - other[4]);
      if (d < BPM_WINDOW) s += W_BPM * (1 - d / BPM_WINDOW);
    }

    if (other[5]) s += W_STOCK;
    return s;
  }

  function shuffle(a) {
    for (var i = a.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = a[i]; a[i] = a[j]; a[j] = t;
    }
    return a;
  }

  /* Draw n items from a scored pool without replacement, each record's chance
     proportional to the square of its score, so the closest matches come up
     most often while the tail still gets its turn. */
  function weightedSample(pool, n) {
    var items = pool.slice();
    var out = [];
    while (out.length < n && items.length) {
      var total = 0, k;
      for (k = 0; k < items.length; k++) total += items[k].w;
      if (total <= 0) break;
      var r = Math.random() * total, acc = 0, pick = items.length - 1;
      for (k = 0; k < items.length; k++) {
        acc += items[k].w;
        if (r <= acc) { pick = k; break; }
      }
      out.push(items[pick].rec);
      items.splice(pick, 1);
    }
    return out;
  }

  /* Last resort, when we know nothing at all about the record being viewed:
     a random draw of in-stock titles. Not a claim of similarity, but the
     section must never vanish — an empty shelf reads as a broken page. */
  function fallbackSample(data, handle) {
    var pool = [];
    for (var i = 0; i < data.r.length; i++) {
      if (data.r[i][5] && data.r[i][0] !== handle) pool.push(data.r[i][0]);
    }
    shuffle(pool);
    return pool.slice(0, SHOW);
  }

  function normArtist(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  }

  /* Build a scoring record for a product the index has never seen.

     The index was exported at a point in time; anything added to the shop since
     is absent from it, and those are exactly the records a shopper is most
     likely to be looking at. Rather than show them nothing, we assemble the
     same six fields from what the page itself knows.

     Genre is matched by WORD here, not by whole token. The index speaks in
     phrases like "deep techno" and "peak time", while a product's own metafields
     and tags say "Techno" and "Acid House", so exact token equality would find
     nothing; matching "techno" inside "deep techno" relates them honestly. */
  function synthesiseMe(root, data) {
    var labelId = -1, artistId = -1, k;
    var label = (root.dataset.label || '').trim();
    if (label) {
      for (k = 0; k < data.l.length; k++) {
        if (data.l[k].toLowerCase() === label.toLowerCase()) { labelId = k; break; }
      }
    }
    var artist = normArtist(root.dataset.artist);
    if (artist) {
      for (k = 0; k < data.a.length; k++) {
        if (data.a[k] === artist) { artistId = k; break; }
      }
    }

    var words = (root.dataset.words || '').toLowerCase().split(';')
      .map(function (w) { return w.trim(); })
      .filter(function (w) { return w.length > 2; });
    /* Exact hits first. "Acid House" lands on the token "acid house", which is
       worth far more than the several dozen tokens merely CONTAINING "house" —
       match those too broadly and every house record in the shop ties on score,
       which is a random draw wearing a similarity badge. The loose pass only
       runs when the specific one came up short. */
    var exact = [], loose = [];
    for (k = 0; k < data.t.length; k++) {
      var tok = data.t[k];
      var hit = 0;                          // 2 = exact, 1 = substring
      for (var w = 0; w < words.length; w++) {
        if (tok === words[w]) { hit = 2; break; }
        /* no break — a later word may still match this token exactly, and
           "acid" must not claim the token "acid house" before "acid house"
           itself gets a look at it */
        if (tok.indexOf(words[w]) !== -1) hit = 1;
      }
      if (hit === 2) exact.push(k);
      else if (hit === 1) loose.push(k);
    }
    /* Kept apart rather than merged: what the record actually says it is, and
       what we merely inferred from a one-word tag, are not the same quality of
       evidence and are not scored the same. */

    var bpm = 0;
    var m = (root.dataset.bpm || '').match(/\d{2,3}/);
    if (m) bpm = parseInt(m[0], 10);

    if (labelId < 0 && artistId < 0 && !exact.length && !loose.length && !bpm) return null;
    return ['', labelId, artistId, exact, bpm, 1, loose];
  }

  function recommend(handle, root) {
    var data = window.PN_RECO;
    if (!data || !data.r) return [];
    markPlaceholders(data);

    var me = null, i;
    for (i = 0; i < data.r.length; i++) {
      if (data.r[i][0] === handle) { me = data.r[i]; break; }
    }
    if (!me) me = synthesiseMe(root, data);
    if (!me) return fallbackSample(data, handle);

    var scored = [];
    for (i = 0; i < data.r.length; i++) {
      var other = data.r[i];
      if (other[0] === handle) continue;
      var s = score(me, other);
      if (s > 0) scored.push({ rec: other, s: s });
    }
    if (!scored.length) return fallbackSample(data, handle);

    scored.sort(function (a, b) { return b.s - a.s; });

    var floor = scored[0].s * FLOOR_RATIO;
    var strong = [];
    for (i = 0; i < scored.length && strong.length < POOL; i++) {
      if (scored[i].s >= floor) strong.push(scored[i]);
    }
    /* A short shelf of real matches beats a full one padded with strangers, so
       when only ten records clear the bar the section simply shows ten and the
       arrows page through fewer. Only if we cannot even fill one row do we fall
       back to the plain top few, so the shelf never looks broken. */
    var top = strong.length >= PER_PAGE_DESKTOP
      ? strong
      : scored.slice(0, PER_PAGE_DESKTOP);

    /* The few strongest matches — same artist on the same label at the same
       tempo — are always in the set, or a shopper could visit and never be
       shown the one record that obviously belongs next to this one. Everything
       after them is drawn at random, and the whole list is then shuffled so the
       anchors do not sit in the same slots on every visit. */
    var out = top.slice(0, ANCHOR).map(function (e) { return e.rec[0]; });
    var rest = top.slice(ANCHOR).map(function (e) { return { rec: e.rec, w: e.s * e.s }; });
    weightedSample(rest, SHOW - out.length).forEach(function (r) { out.push(r[0]); });
    return shuffle(out);
  }

  // ── card fetch + render ───────────────────────────────────────────────

  var cardCache = {};

  function fetchCard(handle) {
    if (cardCache[handle]) return cardCache[handle];
    cardCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    return cardCache[handle];
  }

  function buildCard(p, blankCover) {
    var names = splitTitle(p.title, p.vendor);
    var label = (p.vendor || '').replace(/^Label:\s*/i, '').trim();
    var sold = p.available === false;
    var v = p.variants && p.variants[0];
    var price = money(v ? v.price : p.price);
    var img = p.featured_image
      ? '<img class="pn-record-cover" src="' + esc(p.featured_image) + '" alt="' + esc(p.title) + '" loading="lazy" width="533" height="533">'
      : '<img class="pn-record-cover" src="' + esc(blankCover) + '" alt="No cover" width="533" height="533">';

    return '<li>' +
      '<a href="/products/' + esc(p.handle) + '" class="pn-record-card' + (sold ? ' pn-record-card--sold' : '') + '" aria-label="' + esc(p.title) + '">' +
        '<div class="pn-record-cover-wrap">' + img +
          (sold ? '<div class="pn-record-sold-badge">Sold out</div>' : '') +
          '<div class="pn-record-hover">' +
            '<div class="pn-rh-artist">' + esc(names.artist) + '</div>' +
            '<div class="pn-rh-album">' + esc(names.album) + '</div>' +
            '<div class="pn-rh-divider"></div>' +
            '<div class="pn-rh-price">' + esc(price) + '</div>' +
            (sold
              ? '<span class="pn-rh-btn pn-rh-btn--sold">Sold Out</span>'
              : '<span class="pn-rh-btn" role="button" tabindex="0" data-variant-id="' + (v ? v.id : 0) + '" onclick="pnCardAtc(event,this)">Add to Cart</span>') +
          '</div>' +
        '</div>' +
        '<div class="pn-record-info">' +
          '<div class="pn-record-artist">' + esc(names.artist) + '</div>' +
          '<div class="pn-record-album">' + esc(names.album) + '</div>' +
          (label ? '<div class="pn-record-label">' + esc(label) + '</div>' : '') +
          '<div class="pn-record-price' + (sold ? ' pn-record-price--sold' : '') + '">' + esc(price) + '</div>' +
          (sold ? '<span class="pn-badge pn-badge--sold">Sold Out</span>' : '') +
        '</div>' +
      '</a></li>';
  }

  function init(root) {
    var grid = root.querySelector('.pn-related-grid');
    var btnPrev = root.querySelector('.pn-rel-arrow--prev');
    var btnNext = root.querySelector('.pn-rel-arrow--next');
    var blankCover = root.dataset.blankCover;
    var handle = root.dataset.productHandle;

    var perPage = window.innerWidth <= 749 ? PER_PAGE_MOBILE : PER_PAGE_DESKTOP;
    var handles = recommend(handle, root);
    if (!handles.length) { root.style.display = 'none'; return; }

    var page = 0;
    var pages = Math.ceil(handles.length / perPage);

    function slice(idx) {
      return handles.slice(idx * perPage, idx * perPage + perPage);
    }

    function render(idx, animate) {
      var want = slice(idx);
      Promise.all(want.map(fetchCard)).then(function (products) {
        var live = products.filter(Boolean);
        var html = live.map(function (p) { return buildCard(p, blankCover); }).join('');
        if (animate) {
          grid.style.opacity = '0';
          setTimeout(function () { grid.innerHTML = html; grid.style.opacity = '1'; }, 180);
        } else {
          grid.innerHTML = html;
        }
        btnPrev.disabled = idx === 0;
        btnNext.disabled = idx >= pages - 1;
        slice(idx + 1).forEach(fetchCard);          // prefetch the next page
      });
    }

    btnPrev.addEventListener('click', function () { if (page > 0) render(--page, true); });
    btnNext.addEventListener('click', function () { if (page < pages - 1) render(++page, true); });

    var touchStartX = 0;
    root.addEventListener('touchstart', function (e) {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    root.addEventListener('touchend', function (e) {
      var diff = touchStartX - e.changedTouches[0].screenX;
      if (Math.abs(diff) < 40) return;
      if (diff > 0 && page < pages - 1) render(++page, true);
      else if (diff < 0 && page > 0) render(--page, true);
    }, { passive: true });

    render(0, false);
  }

  ready(function () {
    document.querySelectorAll('.pn-related-section').forEach(init);
  });

  /* Add-to-cart from a card — unchanged behaviour, defined once. */
  if (!window.pnCardAtc) {
    window.pnCardAtc = function (e, btn) {
      e.preventDefault(); e.stopPropagation();
      if (btn.dataset.state === 'loading') return;
      btn.dataset.state = 'loading';
      var orig = btn.textContent;
      btn.textContent = '...';
      var cartEl = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      var sections = cartEl ? cartEl.getSectionsToRender().map(function (s) { return s.id; }) : ['cart-icon-bubble'];
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          id: parseInt(btn.dataset.variantId, 10), quantity: 1,
          sections: sections, sections_url: window.location.pathname
        })
      }).then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.status) {
            var msg = (data.description || data.message || '').toLowerCase();
            btn.textContent = (msg.indexOf('already in your cart') !== -1 || msg.indexOf('all 1') !== -1)
              ? 'Only 1 copy' : 'Error';
          } else {
            btn.textContent = 'Added!';
            if (cartEl) cartEl.renderContents(data);
            fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
              var lbl = document.querySelector('.pn-cart-label');
              if (lbl) lbl.textContent = cart.item_count > 0 ? 'Cart (' + cart.item_count + ')' : 'Cart';
            });
          }
          setTimeout(function () { btn.textContent = orig; delete btn.dataset.state; }, 1800);
        })
        .catch(function () {
          btn.textContent = 'Error';
          setTimeout(function () { btn.textContent = orig; delete btn.dataset.state; }, 1800);
        });
    };
  }
})();
